import { Observable, forkJoin, map } from 'rxjs';
import { BranchRepository } from './branch.repository';
import { IBranchModel } from '../models/branch.model';
import { HttpService } from './../../../services/http-service.service';
import { BranchImplementationRepositoryMapper } from '../mappers/branch-repository.mapper';
import { IBranchEntity } from '../entity/branch.entity';
import { Injectable } from '@angular/core';
import { IBranchExchangeModel } from '../models/branch-exchange.model';
import { IBranchExchangeEntity } from '../entity/branch-exchange.entity';
import { BranchExchangeImplementationRepositoryMapper } from '../mappers/branch-echange.mapper';
import { GeolocationService } from './../../../services/geolocation.service';
import { ExchangeService } from 'src/app/services/exchange.service';

interface BranchesResponseItems {
  items?: IBranchEntity[];
}

interface BranchesApiResponse {
  success: boolean;
  message?: string;
  data: IBranchEntity[] | BranchesResponseItems;
}

interface GeolocationCoordinatesLike {
  latitude?: number | null;
  longitude?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class BranchImplementationRepository extends BranchRepository {
  branchMapper = new BranchImplementationRepositoryMapper();
  branchExchangeMapper = new BranchExchangeImplementationRepositoryMapper();

  constructor(
    private readonly _http: HttpService,
    private readonly _geoLocation: GeolocationService,
    private readonly exchangeService: ExchangeService,
  ) {
    super();
  }

  override getBranchById(id: number): Observable<IBranchModel> {
    return this._http.get<IBranchEntity>(`/branch/${id}`).pipe(
      map((data: IBranchEntity) => {
        return this.branchMapper.mapFrom(data);
      }),
    );
  }

  override getBranches(): Observable<IBranchModel[]> {
    return forkJoin([
      this._http.get<BranchesApiResponse>('/branches'),
      this._geoLocation.getCurrentPosition(),
    ]).pipe(
      map(
        ([branchResponse, position]: [
          BranchesApiResponse,
          GeolocationCoordinatesLike,
        ]) => {
          const branchEntities = this.extractBranchEntities(branchResponse);

          const branchesMap = branchEntities.map((branchEntity) =>
            this.branchMapper.mapFrom(branchEntity),
          );

          if (branchesMap.length === 0) {
            return branchesMap;
          }

          this.getBranchesDistance(branchesMap, position);

          const branchDefault = this.findDefaultBranch(branchesMap);

          this.persistBranches(branchesMap, branchDefault);

          return branchesMap;
        },
      ),
    );
  }

  override getBranchesExchange(
    id_branche: number,
  ): Observable<IBranchExchangeModel> {
    const branchName = this.resolveBranchName(id_branche);

    return this.exchangeService
      .getBranchExchangeRates(id_branche, branchName)
      .pipe(
        map((data: IBranchExchangeModel) => {
          return this.branchExchangeMapper.mapFrom({
            id: data.id,
            branchName: data.name,
            exchange: data.exchange,
          });
        }),
      );
  }

  getBranchesDistance(
    branches: IBranchModel[],
    position: GeolocationCoordinatesLike,
  ) {
    const originLatitude = this.parseCoordinate(position?.latitude);
    const originLongitude = this.parseCoordinate(position?.longitude);

    for (const branch of branches) {
      const branchLatitude = this.parseCoordinate(branch.latitude);
      const branchLongitude = this.parseCoordinate(branch.longitude);

      if (
        originLatitude === null ||
        originLongitude === null ||
        branchLatitude === null ||
        branchLongitude === null
      ) {
        branch.distance = 0;
        continue;
      }

      branch.distance = this.getDistanciaMetros(
        originLatitude,
        originLongitude,
        branchLatitude,
        branchLongitude,
      );
    }
  }

  private resolveBranchName(branchId: number): string | undefined {
    try {
      const branchesRaw = localStorage.getItem('branches');
      if (!branchesRaw) {
        return undefined;
      }
      const branches: Array<Partial<IBranchModel> & { branchName?: string }> =
        JSON.parse(branchesRaw);
      const branch = branches.find((item) => item?.id === branchId);
      return branch?.name ?? branch?.branchName;
    } catch {
      return undefined;
    }
  }

  private findDefaultBranch(
    branches: IBranchModel[],
  ): IBranchModel | undefined {
    const shortestDistance = Math.min(
      ...branches.map((branch) => branch.distance),
    );
    return branches.find((branch) => branch.distance === shortestDistance);
  }

  private extractBranchEntities(
    branchResponse: BranchesApiResponse,
  ): IBranchEntity[] {
    if (Array.isArray(branchResponse.data)) {
      return branchResponse.data;
    }

    if (
      this.isBranchItemsResponse(branchResponse.data) &&
      Array.isArray(branchResponse.data.items)
    ) {
      return branchResponse.data.items;
    }

    return [];
  }

  private isBranchItemsResponse(
    data: IBranchEntity[] | BranchesResponseItems,
  ): data is BranchesResponseItems {
    return !Array.isArray(data);
  }

  private persistBranches(
    branches: IBranchModel[],
    branchDefault?: IBranchModel,
  ): void {
    const storedBranchId = localStorage.getItem('branchId');
    const firstBranchId = branches[0]?.id;
    const preferredBranch = branchDefault ?? branches[0];

    if (!preferredBranch) {
      return;
    }

    const shouldUpdateStorage =
      !storedBranchId || firstBranchId === Number.parseInt(storedBranchId, 10);

    if (!shouldUpdateStorage) {
      return;
    }

    localStorage.setItem('branch', JSON.stringify(preferredBranch));
    localStorage.setItem('branchId', JSON.stringify(preferredBranch.id));
    localStorage.setItem('branches', JSON.stringify(branches));
  }

  private parseCoordinate(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = typeof value === 'number' ? value : Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  getDistanciaMetros(lat1: number, lon1: number, lat2: number, lon2: number) {
    let rad = (x: number) => (x * Math.PI) / 180;
    const R = 6378.137;
    const dLat = rad(lat2 - lat1);
    const dLong = rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(rad(lat1)) *
        Math.cos(rad(lat2)) *
        Math.sin(dLong / 2) *
        Math.sin(dLong / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c * 1000;
    return d;
  }
}
